/**
 * Durable stash for the Acomba Desktop launch assertion (the `src` token from `/desktop?src=`).
 *
 * The assertion is normally carried through the OIDC round-trip in the login `state` and read back
 * in /signin-callback. But `signinRedirectCallback()` consumes that state, so if the callback throws
 * (e.g. the backend/identity endpoint is unreachable), the error page loses the assertion. Persisting
 * it here lets the retry resume the desktop flow instead of falling back to the AcombaGo tenant
 * picker — which has no meaning for a single-workspace Acomba Desktop launch.
 *
 * localStorage (not sessionStorage) on purpose: acceo_web.exe may open the callback in a different
 * tab than the one that started the redirect, and sessionStorage is tab-local — same reasoning as the
 * OIDC state store in oidcService.ts.
 *
 * TTL guard: because the stash outlives the tab, an abandoned desktop launch would otherwise leave the
 * assertion in localStorage forever. A later *browser* sign-in on the same profile that hits the same
 * callback-throws failure would land on /signin-error with no router state and wrongly pick up the
 * stale desktop assertion — routing a browser user into the desktop flow (mirror of the bug this stash
 * fixes). This closes the hole regardless of which browser path initiated the redirect (auth guard,
 * token refresh, change-company, post-logout).
 *
 * The TTL also has to cover SignoutCallback.tsx's post-logout resume: that reads back this same stash
 * to carry the desktop context through the next login, and a real work session between launch and
 * logout is hours, not minutes — a short TTL would let it expire mid-session and silently drop the
 * user into the AcombaGO tenant picker on logout. A workday-scale window balances the two: long
 * enough to survive a full session, short enough that a
 * launch abandoned one day doesn't bleed into a browser sign-in the next.
 */

const DESKTOP_ASSERTION_KEY = 'desktop-assertion';

/** How long a stashed assertion stays honourable — must outlast a full work session (see doc comment above). */
const DESKTOP_ASSERTION_TTL_MS = 12 * 60 * 60 * 1000;

interface StoredAssertion {
    value: string;
    /** Epoch ms when the assertion was stashed; used to expire it past DESKTOP_ASSERTION_TTL_MS. */
    storedAt: number;
}

/** Persists the desktop launch assertion with a timestamp. Storage failures are non-fatal (retry convenience only). */
export function setDesktopAssertion(assertion: string): void {
    try {
        const entry: StoredAssertion = { value: assertion, storedAt: Date.now() };
        localStorage.setItem(DESKTOP_ASSERTION_KEY, JSON.stringify(entry));
    } catch {
        // localStorage can throw in Safari private mode / on quota — a stored assertion is only a
        // retry convenience, not correctness-critical, so swallow and continue.
    }
}

/**
 * Returns the persisted desktop assertion, or null when absent, expired, unparseable, or storage is
 * unavailable. An expired entry is cleared so it cannot resurface on a later read.
 */
export function getDesktopAssertion(): string | null {
    try {
        const raw = localStorage.getItem(DESKTOP_ASSERTION_KEY);
        if (raw === null) return null;

        const entry = JSON.parse(raw) as Partial<StoredAssertion>;
        if (typeof entry.value !== 'string' || typeof entry.storedAt !== 'number') return null;

        if (Date.now() - entry.storedAt > DESKTOP_ASSERTION_TTL_MS) {
            clearDesktopAssertion();
            return null;
        }
        return entry.value;
    } catch {
        // Missing, unparseable (e.g. a legacy plain-string value), or storage unavailable — treat as absent.
        return null;
    }
}

/** Removes the persisted desktop assertion. Called once a sign-in resolves (success or confirmed browser flow). */
export function clearDesktopAssertion(): void {
    try {
        localStorage.removeItem(DESKTOP_ASSERTION_KEY);
    } catch {
        // See setDesktopAssertion — storage failures are non-fatal.
    }
}
